import React from 'react';
import { useUpdateNotification } from '@dashboard/hooks/useUpdateNotification';
import { sanityClient } from '@dashboard/lib/sanity/client';
import { useState, useEffect } from 'react';
import { XMarkIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { toPlainText } from '@portabletext/react';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import Button from '@dashboard/components/Button';
import { urlForImageWithSize } from '@/dashboard/lib/sanity/image';

export default function WhatsNew() {
  const { hasNewUpdates, markUpdatesAsSeen } = useUpdateNotification();
  const [latestUpdate, setLatestUpdate] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const fetchLatestUpdate = async () => {
      const update = await sanityClient.fetch(
        `*[_type == "changelog-entry"] | order(date desc)[0]{
          _id,
          title,
          date,
          body[] {
            ...,
            _type == "image" => {
              ...,
              asset->{
                ...
              }
            },
            markDefs[]{
              ...,
              _type == "internalLink" => {
                "slug": @.reference->slug,
                "type": @.reference->_type
              }
            }
          }
        }`,
      );

      setLatestUpdate(update);

      // Show card if there are new updates
      if (hasNewUpdates && update) {
        setIsVisible(true);
      }
    };

    fetchLatestUpdate();
  }, [hasNewUpdates]);

  if (!isVisible || !latestUpdate) return null;

  const plainTextContent = toPlainText(latestUpdate.body);

  const handleClose = () => {
    setIsVisible(false);
    markUpdatesAsSeen();
  };

  return (
    <Card className="mb-6 bg-gray-50 m-4 rounded-lg relative">
      <div className="absolute top-2 right-2">
        <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      <CardContent>
        <div className="flex items-center gap-x-4 h-full">
          {latestUpdate.body.find(block => block._type === 'image') && (
            <div className="max-w-3xs">
              <img
                src={urlForImageWithSize(
                  latestUpdate.body.find(block => block._type === 'image').asset,
                  512,
                )}
                alt={latestUpdate.title}
                className="w-full h-auto rounded-lg"
              />
            </div>
          )}

          <div className="flex flex-col max-w-xl">
            <h3 className="text-xl font-semibold mb-1">{latestUpdate.title}</h3>
            <p className="text-gray-600 text-base line-clamp-2 mb-2">{plainTextContent}</p>

            <div className="">
              <Link to="/updates" onClick={handleClose}>
                <Button appearance="secondary" size="small">
                  Learn more <ArrowRightIcon className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
